import React, { useEffect, useState } from "react";

import { MatrixRain } from "@/components/MatrixRain";

import {
    getAdminProducts,
    getAdminDesigns,
    getAdminAssetPipelineRecords,
    createAdminProduct,
    updateAdminProduct,
    publishAdminProduct,
    updateAdminDesign,
    runAdminPublishPipeline,
} from "@/lib/api";


const navigationGroups = [
    {
        label: "Core",
        items: [
            {
                id: "overview",
                label: "Overview",
                code: "00",
                accent: "green",
            },
        ],
    },
    {
        label: "Catalog",
        items: [
            {
                id: "products",
                label: "Products",
                code: "01",
                accent: "green",
            },
            {
                id: "designs",
                label: "Designs",
                code: "02",
                accent: "green",
            },
            {
                id: "publish",
                label: "Publish",
                code: "03",
                accent: "orange",
            },
        ],
    },
    {
        label: "Creator Network",
        items: [
            {
                id: "creators",
                label: "Creators",
                code: "04",
                accent: "green",
            },
            {
                id: "storefronts",
                label: "Storefronts",
                code: "05",
                accent: "green",
            },
        ],
    },
    {
        label: "Operations",
        items: [
            {
                id: "orders",
                label: "Orders",
                code: "06",
                accent: "red",
            },
            {
                id: "fulfillment",
                label: "Fulfillment",
                code: "07",
                accent: "red",
            },
        ],
    },
    {
        label: "Finance",
        items: [
            {
                id: "sales",
                label: "Sales",
                code: "08",
                accent: "orange",
            },
            {
                id: "commissions",
                label: "Commissions",
                code: "09",
                accent: "orange",
            },
            {
                id: "margins",
                label: "Margins",
                code: "10",
                accent: "orange",
            },
        ],
    },
];

const dashboardSections = {
    overview: {
        label: "Overview",
        code: "00",
    },
    products: {
        label: "Products",
        code: "01",
    },
    designs: {
        label: "Designs",
        code: "02",
    },
    publish: {
        label: "Publish",
        code: "03",
    },
    creators: {
        label: "Creators",
        code: "04",
    },
    storefronts: {
        label: "Storefronts",
        code: "05",
    },
    orders: {
        label: "Orders",
        code: "06",
    },
    fulfillment: {
        label: "Fulfillment",
        code: "07",
    },
    sales: {
        label: "Sales",
        code: "08",
    },
    commissions: {
        label: "Commissions",
        code: "09",
    },
    margins: {
        label: "Margins",
        code: "10",
    },
};



const getAccentClasses = (accent) => {

    if (accent === "red") {
        return {
            text: "text-matrix-red",
            border: "border-matrix-red/60",
            background: "bg-matrix-red/[0.08]",
            hover: "hover:bg-matrix-red/[0.05]",
            glow: "hover:shadow-[0_0_20px_rgba(255,0,0,0.10)]",
        };
    }

    if (accent === "orange") {
        return {
            text: "text-matrix-orange",
            border: "border-matrix-orange/60",
            background: "bg-matrix-orange/[0.08]",
            hover: "hover:bg-matrix-orange/[0.05]",
            glow: "hover:shadow-[0_0_20px_rgba(255,140,0,0.10)]",
        };
    }

    return {
        text: "text-matrix-green",
        border: "border-matrix-green/60",
        background: "bg-matrix-green/[0.08]",
        hover: "hover:bg-matrix-green/[0.05]",
        glow: "hover:shadow-[0_0_20px_rgba(0,255,136,0.10)]",
    };
};


function Dashboard() {

    const [activeMode, setActiveMode] = useState("overview");

    const [products, setProducts] = useState([]);

    const [productsLoading, setProductsLoading] =
        useState(false);
    
    const [productsError, setProductsError] =
        useState(null);

    const [designs, setDesigns] = useState([]);

    const [designsLoading, setDesignsLoading] =
        useState(false);

    const [designsError, setDesignsError] =
        useState(null);

    const [assetPipelineRecords, setAssetPipelineRecords] =
        useState([]);

    const [assetPipelineLoading, setAssetPipelineLoading] =
        useState(false);

    const [assetPipelineError, setAssetPipelineError] =
        useState(null);

    const activeItem = navigationGroups
        .flatMap((group) => group.items)
        .find((item) => item.id === activeMode);


    useEffect(() => {

        if (activeMode !== "products") {
            return;
        }

        let cancelled = false;

        const loadProducts = async () => {

            setProductsLoading(true);
            setProductsError(null);

            try {


        const response =
            await getAdminProducts();

        const target = (response.products || []).find(
            (product) =>
                product.product_id ===
                "1bc7bc3923d24e32afd6570c4f27812c"
        );

        if (target) {
            console.log("COMIK RELEAF VARIANT DIAGNOSTIC", {
                product_id: target.product_id,
                title: target.title,
                status: target.status,
                production_artwork: target.production_artwork,
                variant_count: target.variants?.length ?? 0,
                variants: (target.variants || []).map((v) => ({
                    color: v.color,
                    size: v.size,
                    status: v.status,
                    printgeek: v.printgeek
                        ? {
                            sku: v.printgeek.sku,
                            product: v.printgeek.product,
                            properties: v.printgeek.properties,
                        }
                        : null,
                })),
            });
        } else {
            console.warn(
                "Target product not present in admin product list."
            );
        }

        if (cancelled) {
            return;
        }


                setProducts(
                    response.products || []
                );

            } catch (error) {

                if (cancelled) {
                    return;
                }

                setProductsError(
                    error?.response?.data?.detail ||
                    error?.message ||
                    "Unable to load products."
                );

            } finally {

                if (!cancelled) {
                    setProductsLoading(false);
                }

            }

        };

        loadProducts();

        return () => {
            cancelled = true;
        };

    }, [activeMode]);


        useEffect(() => {

        if (activeMode !== "designs") {
            return;
        }

        let cancelled = false;

        const loadDesigns = async () => {

            setDesignsLoading(true);
            setDesignsError(null);

            try {

                const response = await getAdminDesigns();

                if (cancelled) {
                    return;
                }

                setDesigns(response.designs || []);

            } catch (error) {

                if (cancelled) {
                    return;
                }

                setDesignsError(
                    error?.response?.data?.detail ||
                    error?.message ||
                    "Unable to load designs."
                );

            } finally {

                if (!cancelled) {
                    setDesignsLoading(false);
                }

            }

        };

        loadDesigns();

        return () => {
            cancelled = true;
        };

    }, [activeMode]);

        useEffect(() => {
        if (activeMode !== "publish") {
            return;
        }

        let cancelled = false;

        const loadAssetPipelineRecords = async () => {
            setAssetPipelineLoading(true);
            setAssetPipelineError(null);

            try {
                const response = await getAdminAssetPipelineRecords();

                if (!cancelled) {
                    setAssetPipelineRecords(response.records || []);
                }
            } catch (error) {
                if (!cancelled) {
                    setAssetPipelineError(
                        error?.response?.data?.detail ||
                        error?.message ||
                        "Unable to load asset pipeline records."
                    );
                }
            } finally {
                if (!cancelled) {
                    setAssetPipelineLoading(false);
                }
            }
        };

        loadAssetPipelineRecords();

        return () => {
            cancelled = true;
        };
    }, [activeMode]);


    return (
        <div
            className="
                relative
                min-h-screen
                overflow-hidden
                bg-black
                text-white
                grain
                scanlines
            "
        >

            <MatrixRain />


            {/* ========================================================== */}
            {/* GLOBAL ATMOSPHERE */}
            {/* ========================================================== */}

            <div
                className="
                    pointer-events-none
                    fixed
                    inset-0
                    z-[1]
                "
                style={{
                    background:
                        "radial-gradient(120% 90% at 50% 0%, rgba(0,0,0,0.12), rgba(0,0,0,0.90) 78%)",
                }}
                aria-hidden
            />


            <div
                className="
                    pointer-events-none
                    fixed
                    left-1/2
                    top-0
                    z-[1]
                    h-[650px]
                    w-[900px]
                    -translate-x-1/2
                    bg-matrix-green/[0.025]
                    blur-[120px]
                "
                aria-hidden
            />


            {/* ========================================================== */}
            {/* APPLICATION */}
            {/* ========================================================== */}

            <div
                className="
                    relative
                    z-10
                    flex
                    min-h-screen
                "
            >


                {/* ====================================================== */}
                {/* SIDEBAR */}
                {/* ====================================================== */}

                <aside
                    className="
                        hidden
                        w-64
                        shrink-0
                        border-r
                        border-matrix-green/10
                        bg-black/70
                        lg:flex
                        lg:flex-col
                    "
                >

                    {/* Sidebar Header */}

                    <div
                        className="
                            border-b
                            border-matrix-green/10
                            px-6
                            py-6
                        "
                    >

                        <div
                            className="
                                flex
                                items-center
                                gap-3
                                font-mono
                                text-[9px]
                                uppercase
                                tracking-[0.3em]
                                text-matrix-green/60
                            "
                        >

                            <span
                                className="
                                    inline-block
                                    h-2
                                    w-2
                                    animate-glow-pulse
                                    rounded-full
                                    bg-matrix-green
                                "
                            />

                            <span>
                                COMIK RELEAF
                            </span>

                        </div>


                        <h1
                            className="
                                mt-3
                                font-display
                                text-xl
                                font-black
                                uppercase
                                tracking-tight
                                text-white
                            "
                        >
                            Admin
                        </h1>


                        <p
                            className="
                                mt-1
                                font-mono
                                text-[8px]
                                uppercase
                                tracking-[0.2em]
                                text-white/20
                            "
                        >
                            Control Interface
                        </p>

                    </div>


                    {/* Navigation */}

                    <nav
                        className="
                            flex-1
                            overflow-y-auto
                            px-3
                            py-5
                        "
                    >

                        {navigationGroups.map((group) => (

                            <div
                                key={group.label}
                                className="
                                    mb-7
                                "
                            >

                                <div
                                    className="
                                        mb-2
                                        px-3
                                        font-mono
                                        text-[8px]
                                        uppercase
                                        tracking-[0.25em]
                                        text-white/20
                                    "
                                >
                                    {group.label}
                                </div>


                                <div
                                    className="
                                        space-y-1
                                    "
                                >

                                    {group.items.map((item) => {

                                        const isActive =
                                            activeMode === item.id;

                                        const accent =
                                            getAccentClasses(
                                                item.accent
                                            );


                                        return (
                                            <button
                                                key={item.id}
                                                type="button"
                                                onClick={() =>
                                                    setActiveMode(
                                                        item.id
                                                    )
                                                }
                                                className={`
                                                    group
                                                    relative
                                                    flex
                                                    w-full
                                                    items-center
                                                    gap-3
                                                    border
                                                    px-3
                                                    py-2.5
                                                    text-left
                                                    transition-all
                                                    duration-200
                                                    ${
                                                        isActive
                                                            ? `${accent.border} ${accent.background} ${accent.text} ${accent.glow}`
                                                            : `
                                                                border-transparent
                                                                text-white/40
                                                                hover:border-white/10
                                                                hover:bg-white/[0.025]
                                                                hover:text-white/70
                                                            `
                                                    }
                                                `}
                                            >

                                                {isActive && (
                                                    <span
                                                        className={`
                                                            absolute
                                                            left-0
                                                            top-0
                                                            h-full
                                                            w-[2px]
                                                            ${accent.background}
                                                        `}
                                                    />
                                                )}


                                                <span
                                                    className={`
                                                        w-5
                                                        font-mono
                                                        text-[8px]
                                                        ${
                                                            isActive
                                                                ? accent.text
                                                                : "text-white/15"
                                                        }
                                                    `}
                                                >
                                                    {item.code}
                                                </span>


                                                <span
                                                    className="
                                                        font-mono
                                                        text-[9px]
                                                        uppercase
                                                        tracking-[0.12em]
                                                    "
                                                >
                                                    {item.label}
                                                </span>


                                                {isActive && (
                                                    <span
                                                        className="
                                                            ml-auto
                                                            h-1.5
                                                            w-1.5
                                                            rounded-full
                                                            bg-current
                                                        "
                                                    />
                                                )}

                                            </button>
                                        );

                                    })}

                                </div>

                            </div>

                        ))}

                    </nav>


                    {/* Sidebar Footer */}

                    <div
                        className="
                            border-t
                            border-matrix-green/10
                            p-4
                        "
                    >

                        <div
                            className="
                                border
                                border-matrix-green/10
                                bg-matrix-green/[0.025]
                                p-3
                            "
                        >

                            <div
                                className="
                                    flex
                                    items-center
                                    gap-2
                                "
                            >

                                <span
                                    className="
                                        h-1.5
                                        w-1.5
                                        animate-glow-pulse
                                        rounded-full
                                        bg-matrix-green
                                    "
                                />


                                <span
                                    className="
                                        font-mono
                                        text-[8px]
                                        uppercase
                                        tracking-[0.2em]
                                        text-matrix-green/60
                                    "
                                >
                                    System Online
                                </span>

                            </div>


                            <p
                                className="
                                    mt-2
                                    font-mono
                                    text-[7px]
                                    uppercase
                                    tracking-wider
                                    text-white/20
                                "
                            >
                                Admin session active
                            </p>

                        </div>

                    </div>

                </aside>


                {/* ====================================================== */}
                {/* MAIN WORKSPACE */}
                {/* ====================================================== */}

                <div
                    className="
                        flex
                        min-w-0
                        flex-1
                        flex-col
                    "
                >


                    {/* ================================================== */}
                    {/* TOP BAR */}
                    {/* ================================================== */}

                    <header
                        className="
                            border-b
                            border-matrix-green/10
                            bg-black/60
                            backdrop-blur-md
                        "
                    >

                        <div
                            className="
                                flex
                                min-h-[82px]
                                items-center
                                justify-between
                                px-6
                                lg:px-10
                            "
                        >

                            <div>

                                <p
                                    className="
                                        font-mono
                                        text-[8px]
                                        uppercase
                                        tracking-[0.3em]
                                        text-matrix-green/40
                                    "
                                >
                                    // ADMIN CONTROL CENTER
                                </p>


                                <div
                                    className="
                                        mt-2
                                        flex
                                        items-center
                                        gap-3
                                    "
                                >

                                    <h2
                                        className="
                                            font-display
                                            text-xl
                                            font-black
                                            uppercase
                                            tracking-tight
                                            text-white
                                            sm:text-2xl
                                        "
                                    >
                                        {activeItem?.label}
                                    </h2>


                                    <span
                                        className="
                                            font-mono
                                            text-[8px]
                                            text-white/15
                                        "
                                    >
                                        //
                                    </span>


                                    <span
                                        className="
                                            font-mono
                                            text-[8px]
                                            uppercase
                                            tracking-[0.2em]
                                            text-white/25
                                        "
                                    >
                                        {activeItem?.code}
                                    </span>

                                </div>

                            </div>


                            <div
                                className="
                                    hidden
                                    items-center
                                    gap-3
                                    sm:flex
                                "
                            >

                                <span
                                    className="
                                        font-mono
                                        text-[8px]
                                        uppercase
                                        tracking-[0.2em]
                                        text-white/20
                                    "
                                >
                                    Session
                                </span>


                                <span
                                    className="
                                        h-1.5
                                        w-1.5
                                        rounded-full
                                        bg-matrix-green
                                        shadow-[0_0_10px_rgba(0,255,136,0.5)]
                                    "
                                />

                            </div>

                        </div>

                    </header>


                    {/* ================================================== */}
                    {/* MOBILE NAVIGATION */}
                    {/* ================================================== */}

                    <div
                        className="
                            overflow-x-auto
                            border-b
                            border-matrix-green/10
                            bg-black/80
                            lg:hidden
                        "
                    >

                        <div
                            className="
                                flex
                                min-w-max
                                gap-1
                                px-4
                                py-3
                            "
                        >

                            {navigationGroups
                                .flatMap(
                                    (group) =>
                                        group.items
                                )
                                .map((item) => {

                                    const isActive =
                                        activeMode === item.id;

                                    const accent =
                                        getAccentClasses(
                                            item.accent
                                        );


                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            onClick={() =>
                                                setActiveMode(
                                                    item.id
                                                )
                                            }
                                            className={`
                                                border
                                                px-3
                                                py-2
                                                font-mono
                                                text-[8px]
                                                uppercase
                                                tracking-[0.12em]
                                                transition-all
                                                ${
                                                    isActive
                                                        ? `${accent.border} ${accent.background} ${accent.text}`
                                                        : `
                                                            border-white/10
                                                            text-white/30
                                                        `
                                                }
                                            `}
                                        >
                                            {item.label}
                                        </button>
                                    );

                                })}

                        </div>

                    </div>


                    {/* ================================================== */}
                    {/* CONTENT */}
                    {/* ================================================== */}

                    <main
                        className="
                            flex-1
                            px-6
                            py-8
                            lg:px-10
                            lg:py-10
                        "
                    >

                        {activeMode === "overview" && (
                            <OverviewView />
                        )}


                        {activeMode === "products" && (
                        <ProductsView
                            products={products}
                            loading={productsLoading}
                            error={productsError}
                            assetPipelineRecords={assetPipelineRecords}
                            onProductUpdated={(updatedProduct) => {
                                setProducts((currentProducts) => {
                                    const exists = currentProducts.some(
                                        (product) =>
                                            product.product_id === updatedProduct.product_id
                                    );

                                    return exists
                                        ? currentProducts.map((product) =>
                                            product.product_id === updatedProduct.product_id
                                                ? updatedProduct
                                                : product
                                        )
                                        : [updatedProduct, ...currentProducts];
                                });
                            }}
/>
                        )}



                        {activeMode === "designs" && (
                            <DesignsView
                                designs={designs}
                                loading={designsLoading}
                                error={designsError}
                            />
                        )}



                        {activeMode === "publish" && (
                            <PublishView
                                records={assetPipelineRecords}
                                recordsLoading={assetPipelineLoading}
                                recordsError={assetPipelineError}
                            />
                        )}


                        {activeMode === "creators" && (
                            <PlaceholderView
                                code="04"
                                title="Creators"
                                description="Creator accounts, status, storefront relationships, activity, and creator-level administration."
                                accent="green"
                            />
                        )}


                        {activeMode === "storefronts" && (
                            <PlaceholderView
                                code="05"
                                title="Storefronts"
                                description="Creator storefront lifecycle, activation state, slugs, visibility, and storefront management."
                                accent="green"
                            />
                        )}


                        {activeMode === "orders" && (
                            <PlaceholderView
                                code="06"
                                title="Orders"
                                description="Customer orders, payment state, order lines, fulfillment state, and order administration."
                                accent="red"
                            />
                        )}


                        {activeMode === "fulfillment" && (
                            <PlaceholderView
                                code="07"
                                title="Fulfillment"
                                description="PrintGeek fulfillment, provider status, tracking, webhook events, and delivery state."
                                accent="red"
                            />
                        )}


                        {activeMode === "sales" && (
                            <PlaceholderView
                                code="08"
                                title="Sales"
                                description="Sales activity, revenue reporting, product performance, and storefront performance."
                                accent="orange"
                            />
                        )}


                        {activeMode === "commissions" && (
                            <PlaceholderView
                                code="09"
                                title="Commissions"
                                description="Creator commission calculations, commission history, and payout-related reporting."
                                accent="orange"
                            />
                        )}


                        {activeMode === "margins" && (
                            <PlaceholderView
                                code="10"
                                title="Margins"
                                description="Internal provider costs, revenue, margins, and administrative financial analysis."
                                accent="orange"
                            />
                        )}

                    </main>


                    {/* ================================================== */}
                    {/* FOOTER */}
                    {/* ================================================== */}

                    <footer
                        className="
                            border-t
                            border-matrix-green/10
                            px-6
                            py-5
                            lg:px-10
                        "
                    >

                        <div
                            className="
                                flex
                                flex-col
                                gap-2
                                font-mono
                                text-[7px]
                                uppercase
                                tracking-[0.2em]
                                text-white/15
                                sm:flex-row
                                sm:items-center
                                sm:justify-between
                            "
                        >

                            <span>
                                COMIK RELEAF // ADMIN CONTROL
                            </span>


                            <span
                                className="
                                    text-matrix-green/25
                                "
                            >
                                SYSTEM // READY
                            </span>

                        </div>

                    </footer>

                </div>

            </div>

        </div>
    );
}


/* ====================================================================== */
/* OVERVIEW */
/* ====================================================================== */

function OverviewView() {

    return (
        <div>

            <section
                className="
                    mb-10
                "
            >

                <div
                    className="
                        border-l-2
                        border-matrix-green/50
                        pl-5
                    "
                >

                    <p
                        className="
                            font-mono
                            text-[9px]
                            uppercase
                            tracking-[0.3em]
                            text-matrix-green/60
                        "
                    >
                        // SYSTEM ACCESS GRANTED
                    </p>


                    <h3
                        className="
                            mt-3
                            font-display
                            text-4xl
                            font-black
                            uppercase
                            leading-none
                            tracking-[-0.04em]
                            text-white
                            md:text-6xl
                        "
                    >
                        Overview
                    </h3>


                    <p
                        className="
                            mt-4
                            max-w-2xl
                            font-mono
                            text-xs
                            leading-6
                            text-white/40
                            md:text-sm
                        "
                    >
                        Central command interface for the Comik Releaf
                        creator ecosystem.
                    </p>

                </div>

            </section>


            {/* ========================================================== */}
            {/* METRICS */}
            {/* ========================================================== */}

            <section
                className="
                    grid
                    gap-px
                    overflow-hidden
                    border
                    border-matrix-green/10
                    bg-matrix-green/10
                    sm:grid-cols-2
                    xl:grid-cols-4
                "
            >

                <MetricCard
                    label="Orders"
                    value="—"
                    detail="Awaiting live data"
                    accent="green"
                />


                <MetricCard
                    label="Revenue"
                    value="—"
                    detail="Awaiting live data"
                    accent="green"
                />


                <MetricCard
                    label="Products"
                    value="—"
                    detail="Awaiting catalog"
                    accent="orange"
                />


                <MetricCard
                    label="Creators"
                    value="—"
                    detail="Awaiting network data"
                    accent="green"
                />

            </section>


            {/* ========================================================== */}
            {/* SYSTEM MODULES */}
            {/* ========================================================== */}

            <section
                className="
                    mt-10
                "
            >

                <div
                    className="
                        mb-4
                        flex
                        items-center
                        gap-4
                    "
                >

                    <span
                        className="
                            font-mono
                            text-[9px]
                            uppercase
                            tracking-[0.3em]
                            text-white/25
                        "
                    >
                        System Modules
                    </span>


                    <div
                        className="
                            h-px
                            flex-1
                            bg-matrix-green/10
                        "
                    />

                </div>


                <div
                    className="
                        grid
                        gap-6
                        lg:grid-cols-3
                    "
                >

                    <ModuleCard
                        code="01"
                        label="Catalog"
                        title="Product System"
                        description="Products, variants, provider mappings, publication state, and storefront presentation."
                        accent="green"
                    />


                    <ModuleCard
                        code="02"
                        label="Creative"
                        title="Asset Pipeline"
                        description="Design storage, mockup generation, garment renders, and promotional assets."
                        accent="orange"
                    />


                    <ModuleCard
                        code="03"
                        label="Operations"
                        title="Fulfillment"
                        description="Orders, PrintGeek processing, tracking, webhook state, and delivery."
                        accent="red"
                    />

                </div>

            </section>

        </div>
    );
}


/* ====================================================================== */
/* METRIC CARD */
/* ====================================================================== */

function MetricCard({
    label,
    value,
    detail,
    accent,
}) {

    const colors =
        getAccentClasses(accent);


    return (
        <div
            className="
                bg-black/80
                p-6
                transition-all
                duration-300
                hover:bg-matrix-green/[0.025]
            "
        >

            <p
                className="
                    font-mono
                    text-[8px]
                    uppercase
                    tracking-[0.25em]
                    text-white/30
                "
            >
                {label}
            </p>


            <p
                className={`
                    mt-4
                    font-display
                    text-4xl
                    font-black
                    ${colors.text}
                `}
            >
                {value}
            </p>


            <p
                className="
                    mt-2
                    font-mono
                    text-[8px]
                    uppercase
                    tracking-wider
                    text-white/20
                "
            >
                {detail}
            </p>

        </div>
    );
}


/* ====================================================================== */
/* MODULE CARD */
/* ====================================================================== */

function ModuleCard({
    code,
    label,
    title,
    description,
    accent,
}) {

    const colors =
        getAccentClasses(accent);


    return (
        <div
            className={`
                relative
                overflow-hidden
                border
                bg-black/70
                p-6
                transition-all
                duration-300
                ${colors.border}
                ${colors.hover}
            `}
        >

            <div
                className={`
                    absolute
                    right-0
                    top-0
                    h-12
                    w-12
                    border-r
                    border-t
                    ${colors.border}
                `}
            />


            <div
                className="
                    flex
                    items-center
                    gap-3
                "
            >

                <span
                    className={`
                        font-mono
                        text-[8px]
                        ${colors.text}
                    `}
                >
                    {code}
                </span>


                <span
                    className="
                        font-mono
                        text-[8px]
                        uppercase
                        tracking-[0.25em]
                        text-white/25
                    "
                >
                    {label}
                </span>

            </div>


            <h4
                className="
                    mt-4
                    font-display
                    text-xl
                    font-bold
                    uppercase
                    tracking-wider
                    text-white
                "
            >
                {title}
            </h4>


            <p
                className="
                    mt-3
                    font-mono
                    text-[9px]
                    leading-5
                    text-white/30
                "
            >
                {description}
            </p>

        </div>
    );
}



/* ====================================================================== */
/* PUBLISH */
/* ====================================================================== */

function PublishView({ records, recordsLoading, recordsError }) {
    const [artworkFiles, setArtworkFiles] = useState({
        blackInk: null,
        whiteInk: null,
    });

    const [previewUrls, setPreviewUrls] = useState({
        blackInk: null,
        whiteInk: null,
    });

    const [fileErrors, setFileErrors] = useState({
        blackInk: null,
        whiteInk: null,
    });


    const [pipelineBusy, setPipelineBusy] = useState(false);
    const [pipelineResult, setPipelineResult] = useState(null);

    const [productTitle, setProductTitle] = useState("");
    const [productDescription, setProductDescription] = useState("");
    const [productTags, setProductTags] = useState("");
    const [priceCad, setPriceCad] = useState("");
    const [priceUsd, setPriceUsd] = useState("");

    const publishBlockers = [
        !artworkFiles.blackInk && "Black-ink artwork not loaded",
        !artworkFiles.whiteInk && "White-ink artwork not loaded",
        pipelineBusy && "Pipeline already running",
        !productTitle.trim() && "Product title is blank",
        !productDescription.trim() && "Description is blank",
        priceCad.trim() === "" && "CAD price is blank",
        priceCad.trim() !== "" &&
            (!Number.isFinite(Number(priceCad)) || Number(priceCad) <= 0) &&
            "CAD price is invalid",
        priceUsd.trim() === "" && "USD price is blank",
        priceUsd.trim() !== "" &&
            (!Number.isFinite(Number(priceUsd)) || Number(priceUsd) <= 0) &&
            "USD price is invalid",
        Object.values(fileErrors).some(Boolean) &&
            "An artwork validation error remains",
    ].filter(Boolean);



    const handleArtworkChange = (kind, event) => {
        const file = event.target.files?.[0];

        setFileErrors((previous) => ({
            ...previous,
            [kind]: null,
        }));

        setArtworkFiles((previous) => ({
            ...previous,
            [kind]: null,
        }));

        if (previewUrls[kind]) {
            URL.revokeObjectURL(previewUrls[kind]);

            setPreviewUrls((previous) => ({
                ...previous,
                [kind]: null,
            }));
        }

        if (!file) return;

        if (file.type !== "image/png") {
            setFileErrors((previous) => ({
                ...previous,
                [kind]: "Invalid file type. Please select a PNG image.",
            }));
            event.target.value = "";
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            if (
                image.naturalWidth !== 3600 ||
                image.naturalHeight !== 4800
            ) {
                setFileErrors((previous) => ({
                    ...previous,
                    [kind]: `Expected 3600 × 4800 px; received ${image.naturalWidth} × ${image.naturalHeight} px.`,
                }));

                URL.revokeObjectURL(objectUrl);
                event.target.value = "";
                return;
            }

            setArtworkFiles((previous) => ({
                ...previous,
                [kind]: file,
            }));

            setPreviewUrls((previous) => ({
                ...previous,
                [kind]: objectUrl,
            }));
        };

        image.onerror = () => {
            setFileErrors((previous) => ({
                ...previous,
                [kind]: "Unable to read the selected image.",
            }));

            URL.revokeObjectURL(objectUrl);
            event.target.value = "";
        };

        image.src = objectUrl;
    };


    const handleRunPipeline = async () => {
        const cad = Number(priceCad);
        const usd = Number(priceUsd);

        if (
            !artworkFiles.blackInk ||
            !artworkFiles.whiteInk ||
            pipelineBusy ||
            !productTitle.trim() ||
            !productDescription.trim() ||
            !Number.isFinite(cad) ||
            cad <= 0 ||
            !Number.isFinite(usd) ||
            usd <= 0 ||
            Object.values(fileErrors).some(Boolean)
        ) {
            return;
        }

        setPipelineBusy(true);
        setPipelineResult(null);

        const formData = new FormData();
        formData.append("black_ink", artworkFiles.blackInk);
        formData.append("white_ink", artworkFiles.whiteInk);

        try {
            const result = await runAdminPublishPipeline(formData);

            if (!result || typeof result !== "object") {
                throw new Error(
                    "The backend returned an empty or invalid response. Check the backend terminal."
                );
            }

            if (result.success !== true) {
                throw new Error(
                    result.message ||
                    "The backend did not report successful completion."
                );
            }

            if (!result.product_id) {
                setPipelineResult({
                    success: false,
                    pipelineSucceeded: true,
                    message:
                        "Assets were generated, but the response did not contain a product ID. Product details were not saved.",
                });
                return;
            }

            try {

            await updateAdminProduct(result.product_id, {
                title: productTitle.trim(),
                description: productDescription.trim(),
                tags: [
                    ...new Set(
                        productTags
                            .split(",")
                            .map((tag) => tag.trim())
                            .filter(Boolean)
                            .map((tag) => tag.toLowerCase())
                    ),
                ],
                pricing: {
                    cad,
                    usd,
                },
            });

            } catch (error) {
                const detail = error.response?.data?.detail;

                setPipelineResult({
                    success: false,
                    pipelineSucceeded: true,
                    message:
                        "Assets were generated successfully, but saving the product details failed.\n" +
                        (
                            typeof detail === "string"
                                ? detail
                                : JSON.stringify(
                                    detail ?? error.message,
                                    null,
                                    2
                                )
                        ),
                });
                return;
            }

            if (!result.design_id) {
                throw new Error(
                    "Assets and product details were saved, but the pipeline returned no design ID. Publication was not attempted."
                );
            }

            try {
                await updateAdminDesign(result.design_id, {
                    status: "ACTIVE",
                });
            } catch (error) {
                const detail = error.response?.data?.detail;

                setPipelineResult({
                    success: false,
                    pipelineSucceeded: true,
                    message:
                        "Assets and product details were saved, but activating the design failed. The product was not published.\n" +
                        (
                            typeof detail === "string"
                                ? detail
                                : JSON.stringify(
                                    detail ?? error.message,
                                    null,
                                    2
                                )
                        ),
                });
                return;
            }

            try {
                await publishAdminProduct(result.product_id);
            } catch (error) {
                const detail = error.response?.data?.detail;

                setPipelineResult({
                    success: false,
                    pipelineSucceeded: true,
                    message:
                        "Assets and product details were saved and the design was activated, but publishing the product failed.\n" +
                        (
                            typeof detail === "string"
                                ? detail
                                : JSON.stringify(
                                    detail ?? error.message,
                                    null,
                                    2
                                )
                        ),
                });
                return;
            }

            setPipelineResult({
                success: true,
                message:
                    `Pipeline completed for ${result.design}.\n` +
                    `Product details saved.\n` +
                    `Design activated and product published to MerchWheel.\n` +
                    `CAD: $${cad.toFixed(2)} | USD: $${usd.toFixed(2)}\n` +
                    `Product ID: ${result.product_id}`,
            });
        } catch (error) {
            const detail = error.response?.data?.detail;

            setPipelineResult({
                success: false,
                message:
                    typeof detail === "string"
                        ? detail
                        : JSON.stringify(
                            detail ?? error.message,
                            null,
                            2
                        ),
            });
        } finally {
            setPipelineBusy(false);
        }
    };








    return (
        <div>

            {/* ========================================================== */}
            {/* HEADER */}
            {/* ========================================================== */}

            <section
                className="
                    mb-10
                "
            >

                <div
                    className="
                        border-l-2
                        border-matrix-orange/60
                        pl-5
                    "
                >

                    <p
                        className="
                            font-mono
                            text-[9px]
                            uppercase
                            tracking-[0.3em]
                            text-matrix-orange/70
                        "
                    >
                        // PUBLISH PIPELINE
                    </p>


                    <h3
                        className="
                            mt-3
                            font-display
                            text-4xl
                            font-black
                            uppercase
                            leading-none
                            tracking-[-0.04em]
                            text-white
                            md:text-6xl
                        "
                    >
                        Publish
                    </h3>


                    <p
                        className="
                            mt-4
                            max-w-2xl
                            font-mono
                            text-xs
                            leading-6
                            text-white/40
                            md:text-sm
                        "
                    >
                        Upload a production-ready design and run it
                        through the complete Comik Releaf publishing
                        pipeline.
                    </p>

                </div>

            </section>


            {/* ========================================================== */}
            {/* STOREFRONT PRODUCT DETAILS */}
            {/* ========================================================== */}

            <section className="mt-6 border border-matrix-orange/20 bg-black/60">
                <div className="border-b border-matrix-orange/10 px-4 py-3">
                    <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-matrix-orange/70">
                        Storefront Product Details
                    </p>
                    <p className="mt-2 font-mono text-[9px] text-white/40">
                        These details will be saved to the draft product after the asset pipeline completes.
                    </p>
                </div>

                <div className="grid gap-4 p-4 md:grid-cols-2">
                    <label className="block">
                        <span className="mb-2 block font-mono text-[9px] uppercase tracking-wider text-white/60">
                            Product Title
                        </span>
                        <input
                            type="text"
                            value={productTitle}
                            onChange={(event) => setProductTitle(event.target.value)}
                            maxLength={160}
                            placeholder="Enter storefront product title"
                            className="w-full min-w-0 border border-white/15 bg-black px-3 py-3 font-mono text-xs text-white placeholder:text-white/30 outline-none focus:border-matrix-orange/60"
                        />
                    </label>

                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="mb-2 block font-mono text-[9px] uppercase tracking-wider text-white/60">
                                Canada Price (CAD)
                            </span>
                            <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={priceCad}
                                onChange={(event) => setPriceCad(event.target.value)}
                                placeholder="(enter price)"
                                className="w-full min-w-0 border border-white/15 bg-black px-3 py-3 font-mono text-xs text-white placeholder:text-white/30 outline-none focus:border-matrix-orange/60"
                            />
                        </label>

                        <label className="block">
                            <span className="mb-2 block font-mono text-[9px] uppercase tracking-wider text-white/60">
                                US Price (USD)
                            </span>
                            <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={priceUsd}
                                onChange={(event) => setPriceUsd(event.target.value)}
                                placeholder="(enter price)"
                                className="w-full min-w-0 border border-white/15 bg-black px-3 py-3 font-mono text-xs text-white placeholder:text-white/30 outline-none focus:border-matrix-orange/60"
                            />
                        </label>
                    </div>

                    <label className="block md:col-span-2">
                        <span className="mb-2 block font-mono text-[9px] uppercase tracking-wider text-white/60">
                            Storefront Description
                        </span>
                        <textarea
                            value={productDescription}
                            onChange={(event) => setProductDescription(event.target.value)}
                            rows={4}
                            maxLength={5000}
                            placeholder="Describe the design and product for customers."
                            className="w-full min-w-0 border border-white/15 bg-black px-3 py-3 font-mono text-xs text-white placeholder:text-white/30 outline-none focus:border-matrix-orange/60"
                        />
                    </label>

                    <label className="block md:col-span-2">
                        <span className="mb-2 block font-mono text-[9px] uppercase tracking-wider text-white/60">
                            Storefront Tags // SEO
                        </span>
                        <input
                            type="text"
                            value={productTags}
                            onChange={(event) => setProductTags(event.target.value)}
                            maxLength={1000}
                            placeholder="(enter comma-separated tags)"
                            className="w-full min-w-0 border border-white/15 bg-black px-3 py-3 font-mono text-xs text-white placeholder:text-white/30 outline-none focus:border-matrix-orange/60"
                        />
                        <p className="mt-2 font-mono text-[9px] leading-5 text-white/35">
                            Separate tags with commas. Example: funny shirt, political satire, Canadian humour
                        </p>
                    </label>

                </div>
            </section>




            {/* ========================================================== */}
            {/* DESIGN INPUT */}
            {/* ========================================================== */}

            <section className="border border-matrix-orange/20 bg-black/60">

                <div className="border-b border-matrix-orange/10 px-6 py-4">
                    <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-matrix-orange/60">
                        DESIGN INPUT // TWO REQUIRED FILES
                    </p>
                </div>

                <div className="grid grid-cols-1 gap-3 p-3 md:grid-cols-2 md:gap-4 md:p-4">

                    {[
                        {
                            kind: "blackInk",
                            title: "Black-Ink Artwork",
                            description: "Required for white and grey shirts.",
                        },
                        {
                            kind: "whiteInk",
                            title: "White-Ink Artwork",
                            description: "Required for black shirts.",
                        },
                    ].map((item) => (
                        <div
                            key={item.kind}
                            className="border border-white/10 bg-white/[0.01] p-3"
                        >
                            <div className="mb-4">
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/80">
                                    {item.title}
                                </p>
                                <p className="mt-2 font-mono text-[9px] text-white/40">
                                    {item.description}
                                </p>
                            </div>

                            <label className="block cursor-pointer">
                                <input
                                    type="file"
                                    accept="image/png"
                                    onChange={(event) =>
                                        handleArtworkChange(item.kind, event)
                                    }
                                    className="block w-full min-w-0 font-mono text-[10px] text-white/60 file:mr-2 file:max-w-full file:border file:border-matrix-orange/30 file:bg-matrix-orange/[0.05] file:px-3 file:py-2 file:font-mono file:text-[9px] file:uppercase file:tracking-wider file:text-matrix-orange hover:file:border-matrix-orange/60"
                                />
                            </label>

                            {previewUrls[item.kind] && artworkFiles[item.kind] && (
                                <div className="mt-5 border border-matrix-green/20 bg-black/40 p-4">
                                    <img
                                        src={previewUrls[item.kind]}
                                        alt={artworkFiles[item.kind].name}
                                        className="mx-auto block max-h-[320px] w-auto max-w-full object-contain"
                                    />

                                    <p className="mt-4 break-all font-mono text-[9px] uppercase tracking-wider text-white/70">
                                        {artworkFiles[item.kind].name}
                                    </p>

                                    <p className="mt-2 font-mono text-[8px] uppercase tracking-wider text-matrix-green">
                                        3600 × 4800 PX // VALID
                                    </p>
                                </div>
                            )}

                            {fileErrors[item.kind] && (
                                <div className="mt-4 border border-matrix-red/30 bg-matrix-red/[0.04] px-4 py-3">
                                    <p className="font-mono text-[8px] uppercase tracking-wider text-matrix-red">
                                        // VALIDATION ERROR
                                    </p>
                                    <p className="mt-2 font-mono text-[9px] leading-5 text-white/60">
                                        {fileErrors[item.kind]}
                                    </p>
                                </div>
                            )}

                            {!artworkFiles[item.kind] && !fileErrors[item.kind] && (
                                <p className="mt-4 font-mono text-[8px] uppercase tracking-wider text-white/25">
                                    // AWAITING FILE
                                </p>
                            )}
                        </div>
                    ))}

                    <div className="border border-matrix-orange/20 bg-matrix-orange/[0.03] px-4 py-3">
                        <p className="font-mono text-[9px] uppercase tracking-wider text-matrix-orange">
                            Required before processing
                        </p>
                        <p className="mt-2 font-mono text-[9px] leading-5 text-white/50">
                            Black-ink and white-ink artwork. Both PNG files must pass validation at 3600 × 4800 px before processing.
                        </p>
                    </div>

                </div>
            </section>




            {/* ========================================================== */}
            {/* PIPELINE */}
            {/* ========================================================== */}

            <section
                className="
                    mt-8
                "
            >

                <div
                    className="
                        mb-4
                        flex
                        items-center
                        gap-4
                    "
                >

                    <span
                        className="
                            font-mono
                            text-[9px]
                            uppercase
                            tracking-[0.3em]
                            text-white/25
                        "
                    >
                        Publishing Pipeline
                    </span>


                    <div
                        className="
                            h-px
                            flex-1
                            bg-matrix-green/10
                        "
                    />

                </div>


                <div
                    className="
                        grid
                        gap-px
                        overflow-hidden
                        border
                        border-matrix-green/10
                        bg-matrix-green/10
                        md:grid-cols-3
                    "
                >

                    <PublishStage
                        code="01"
                        title="Design"
                        description="Validate production artwork."
                        accent="orange"
                    />


                    <PublishStage
                        code="02"
                        title="Mockups"
                        description="Generate product mockups."
                        accent="green"
                    />


                    <PublishStage
                        code="03"
                        title="Advertising"
                        description="Generate promotional assets."
                        accent="green"
                    />

                </div>


                <div className="mt-6 flex flex-col gap-4">
                    <button
                        type="button"
                        onClick={handleRunPipeline}
                        disabled={
                            pipelineBusy ||
                            !artworkFiles.blackInk ||
                            !artworkFiles.whiteInk ||
                            Object.values(fileErrors).some(Boolean) ||
                            !productTitle.trim() ||
                            !productDescription.trim() ||
                            !Number.isFinite(Number(priceCad)) ||
                            Number(priceCad) <= 0 ||
                            !Number.isFinite(Number(priceUsd)) ||
                            Number(priceUsd) <= 0
                        }
                        className="
                            border border-matrix-green/40
                            bg-matrix-green/10
                            px-6 py-4
                            font-mono text-xs uppercase tracking-[0.2em]
                            text-matrix-green
                            transition hover:bg-matrix-green/20
                            disabled:cursor-not-allowed
                            disabled:opacity-30
                        "
                    >
                        {pipelineBusy
                            ? "Processing Design..."
                            : "Upload Assets & Run Pipeline"}
                    </button>


                    {publishBlockers.length > 0 && (
                        <div className="border border-red-500/30 bg-red-500/[0.04] p-3 font-mono text-[10px] text-red-400">
                            <p className="mb-2 uppercase tracking-wider">
                                Publish blocked by:
                            </p>
                            {publishBlockers.map((reason) => (
                                <p key={reason}>— {reason}</p>
                            ))}
                        </div>
                    )}


                    {pipelineResult && (
                        <div
                            className={`border p-4 font-mono text-xs whitespace-pre-wrap break-words ${
                                pipelineResult.success
                                    ? "border-matrix-green/30 text-matrix-green"
                                    : "border-red-500/30 text-red-400"
                            }`}
                            role="status"
                        >
                    {pipelineResult.success
                        ? pipelineResult.message
                        : pipelineResult.pipelineSucceeded
                            ? `ASSET PIPELINE SUCCEEDED — PRODUCT DETAILS NEED ATTENTION\n${pipelineResult.message}`
                            : `PIPELINE FAILED\n${pipelineResult.message}`}
                        </div>
                    )}
                </div>


                        </section>

            {/* VERIFIED ASSET RECORDS */}
            <section className="mt-10 border border-matrix-green/20 bg-black/60">
                <div className="border-b border-matrix-green/10 px-6 py-4">
                    <h3 className="font-mono text-sm uppercase tracking-widest text-matrix-green">
                        Verified Asset Records
                    </h3>
                </div>

                <div className="space-y-6 p-6">
                    {recordsLoading && (
                        <p className="font-mono text-xs text-white/50">
                            Loading saved records...
                        </p>
                    )}

                    {recordsError && (
                        <p className="font-mono text-xs text-red-400">
                            {typeof recordsError === "string"
                                ? recordsError
                                : JSON.stringify(recordsError)}
                        </p>
                    )}

                    {!recordsLoading &&
                        !recordsError &&
                        records.length === 0 && (
                            <p className="font-mono text-xs text-white/40">
                                No saved pipeline records found.
                            </p>
                        )}

                    {records.map((record) => (
                        <div
                            key={record.design}
                            className="border border-white/10 p-4"
                        >
                            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                                <h4 className="break-all font-mono text-sm text-white">
                                    {record.design}
                                </h4>

                                <span className="font-mono text-[10px] uppercase tracking-widest text-matrix-green">
                                    {record.verified ? "Verified" : "Unverified"}
                                </span>
                            </div>

                            <div className="space-y-4 font-mono text-xs">
                                <div>
                                    <p className="mb-2 text-matrix-orange">ARTWORK</p>
                                    {Object.entries(record.artwork_keys || {}).map(
                                        ([type, key]) => (
                                            <p key={type} className="break-all py-1 text-white/60">
                                                {type}: {key}
                                            </p>
                                        )
                                    )}
                                </div>

                                <div>
                                    <p className="mb-2 text-matrix-orange">MOCKUPS</p>
                                    {(record.mockup_keys || []).map((key) => (
                                        <p key={key} className="break-all py-1 text-white/60">
                                            {key}
                                        </p>
                                    ))}
                                </div>

                                <div>
                                    <p className="mb-2 text-matrix-orange">ADVERTISING</p>
                                    {(record.advertising_keys || []).map((key) => (
                                        <p key={key} className="break-all py-1 text-white/60">
                                            {key}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

        </div>
    );
}

/* ====================================================================== */
/* PUBLISH STAGE */
/* ====================================================================== */

function PublishStage({
    code,
    title,
    description,
    accent,
}) {

    const colors =
        getAccentClasses(accent);


    return (
        <div
            className="
                bg-black/80
                p-6
            "
        >

            <div
                className="
                    flex
                    items-center
                    gap-3
                "
            >

                <span
                    className={`
                        font-mono
                        text-[8px]
                        ${colors.text}
                    `}
                >
                    {code}
                </span>


                <span
                    className="
                        font-mono
                        text-[8px]
                        uppercase
                        tracking-[0.25em]
                        text-white/25
                    "
                >
                    Stage
                </span>

            </div>


            <h4
                className="
                    mt-4
                    font-display
                    text-xl
                    font-bold
                    uppercase
                    tracking-wider
                    text-white
                "
            >
                {title}
            </h4>


            <p
                className="
                    mt-3
                    font-mono
                    text-[9px]
                    leading-5
                    text-white/30
                "
            >
                {description}
            </p>

        </div>
    );
}

/* ====================================================================== */
/* PLACEHOLDER VIEW */
/* ====================================================================== */

function PlaceholderView({
    code,
    title,
    description,
    accent,
}) {

    const colors =
        getAccentClasses(accent);


    return (
        <div>

            <section
                className="
                    mb-10
                "
            >

                <div
                    className={`
                        border-l-2
                        pl-5
                        ${colors.border}
                    `}
                >

                    <p
                        className={`
                            font-mono
                            text-[9px]
                            uppercase
                            tracking-[0.3em]
                            ${colors.text}
                        `}
                    >
                        // MODULE {code}
                    </p>


                    <h3
                        className="
                            mt-3
                            font-display
                            text-4xl
                            font-black
                            uppercase
                            leading-none
                            tracking-[-0.04em]
                            text-white
                            md:text-6xl
                        "
                    >
                        {title}
                    </h3>


                    <p
                        className="
                            mt-4
                            max-w-2xl
                            font-mono
                            text-xs
                            leading-6
                            text-white/40
                            md:text-sm
                        "
                    >
                        {description}
                    </p>

                </div>

            </section>


            <div
                className="
                    relative
                    min-h-[360px]
                    overflow-hidden
                    border
                    border-matrix-green/10
                    bg-black/60
                "
            >

                <div
                    className="
                        absolute
                        inset-0
                        bg-[linear-gradient(to_bottom,transparent_50%,rgba(0,255,100,0.025)_50%)]
                        bg-[length:100%_4px]
                    "
                />


                <div
                    className="
                        relative
                        flex
                        min-h-[360px]
                        items-center
                        justify-center
                        p-8
                    "
                >

                    <div
                        className="
                            text-center
                        "
                    >

                        <div
                            className={`
                                mx-auto
                                flex
                                h-14
                                w-14
                                items-center
                                justify-center
                                border
                                ${colors.border}
                                ${colors.background}
                            `}
                        >

                            <span
                                className={`
                                    font-mono
                                    text-xs
                                    ${colors.text}
                                `}
                            >
                                {code}
                            </span>

                        </div>


                        <p
                            className="
                                mt-5
                                font-mono
                                text-[9px]
                                uppercase
                                tracking-[0.25em]
                                text-white/25
                            "
                        >
                            Module interface ready
                        </p>


                        <p
                            className="
                                mt-2
                                font-mono
                                text-[8px]
                                uppercase
                                tracking-wider
                                text-white/15
                            "
                        >
                            Backend integration pending
                        </p>

                    </div>

                </div>

            </div>

        </div>
    );
}


function DesignsView({ designs, loading, error }) {
    return (
        <div>
            <div className="mb-6">
                <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-matrix-green/60">
                    // DESIGN CATALOG
                </p>

                <h2 className="mt-2 text-xl font-semibold text-white">
                    Designs
                </h2>

                <p className="mt-2 text-xs text-white/40">
                    Registered designs, lifecycle status, and stored artwork references.
                </p>
            </div>

            {loading && (
                <div className="border border-white/10 px-5 py-8">
                    <p className="font-mono text-xs text-matrix-green/70">
                        LOADING DESIGN RECORDS...
                    </p>
                </div>
            )}

            {!loading && error && (
                <div className="border border-red-500/30 bg-red-500/[0.04] px-5 py-4">
                    <p className="font-mono text-xs text-red-400">
                        DESIGN CATALOG ERROR
                    </p>
                    <p className="mt-2 text-sm text-white/70">
                        {error}
                    </p>
                </div>
            )}

            {!loading && !error && designs.length === 0 && (
                <div className="border border-white/10 px-5 py-8">
                    <p className="font-mono text-xs text-white/70">
                        NO DESIGN RECORDS FOUND
                    </p>
                    <p className="mt-3 text-sm leading-6 text-white/40">
                        The database returned no registered designs. This does not necessarily mean the artwork files are missing from R2.
                    </p>
                </div>
            )}

            {!loading && !error && designs.length > 0 && (
                <div className="overflow-x-auto border border-white/10">
                    <table className="w-full min-w-[700px] text-left">
                        <thead className="border-b border-white/10 bg-white/[0.02]">
                            <tr>
                                {[
                                    "DESIGN",
                                    "STATUS",
                                    "ORIGINAL SOURCE (OPTIONAL)",
                                    "BLACK INK",
                                    "WHITE INK",
                                ].map((heading) => (
                                    <th
                                        key={heading}
                                        className="px-4 py-3 font-mono text-[8px] uppercase tracking-wider text-white/40"
                                    >
                                        {heading}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {designs.map((design) => (
                                <tr
                                    key={design.design_id}
                                    className="border-b border-white/5 last:border-b-0"
                                >
                                    <td className="px-4 py-4">
                                        <p className="text-xs text-white/80">
                                            {design.asset_slug || "Untitled design"}
                                        </p>
                                        <p className="mt-1 font-mono text-[8px] text-white/30">
                                            {design.design_id}
                                        </p>
                                    </td>

                                    <td className="px-4 py-4">
                                        <span className="font-mono text-[9px] text-matrix-green/80">
                                            {design.status || "UNKNOWN"}
                                        </span>
                                    </td>

                                    {[
                                        design.source_key,
                                        design.black_ink_key,
                                        design.white_ink_key,
                                    ].map((assetKey, index) => (
                                        <td
                                            key={index}
                                            className="px-4 py-4"
                                        >
                                            {assetKey ? (
                                                <span
                                                    className="font-mono text-[9px] text-matrix-green/70"
                                                    title={assetKey}
                                                >
                                                    STORED
                                                </span>
                                            ) : (
                                                <span className="font-mono text-[9px] text-white/25">
                                                    MISSING
                                                </span>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!loading && !error && designs.length > 0 && (
                <p className="mt-3 font-mono text-[9px] text-white/30">
                    {designs.length} REGISTERED DESIGN
                    {designs.length === 1 ? "" : "S"}
                </p>
            )}
        </div>
    );
}







function ProductsView({
    products,
    loading,
    error,
    assetPipelineRecords,
    onProductUpdated,
}) {
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [selectedPipelineRecord, setSelectedPipelineRecord] = useState(null);
    const [showCreateProduct, setShowCreateProduct] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const [newProductTitle, setNewProductTitle] = useState("");
    const [newProductCategory, setNewProductCategory] = useState("Apparel");
    const [newProductCadPrice, setNewProductCadPrice] = useState("");
    const [newProductUsdPrice, setNewProductUsdPrice] = useState("");
    const [creatingProduct, setCreatingProduct] = useState(false);
    const [createProductError, setCreateProductError] = useState("");
    const [createProductMessage, setCreateProductMessage] = useState("");

    const [editTitle, setEditTitle] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [editTags, setEditTags] = useState("");
    const [savingMetadata, setSavingMetadata] = useState(false);
    const [metadataError, setMetadataError] = useState("");
    const [metadataMessage, setMetadataMessage] = useState("");

    useEffect(() => {
        setEditTitle(selectedProduct?.title || "");
        setEditDescription(selectedProduct?.description || "");
        setEditTags(
            Array.isArray(selectedProduct?.tags)
                ? selectedProduct.tags.join(", ")
                : ""
        );
        setMetadataError("");
        setMetadataMessage("");
    }, [selectedProduct]);



    const handleSaveMetadata = async () => {
        if (!selectedProduct || savingMetadata) {
            return;
        }

        setSavingMetadata(true);
        setMetadataError("");
        setMetadataMessage("");

        try {
            const updatedProduct = await updateAdminProduct(
                selectedProduct.product_id,
                {
                    title: editTitle.trim(),
                    description: editDescription.trim(),
                    tags: editTags
                        .split(",")
                        .map((tag) => tag.trim())
                        .filter(Boolean),
                }
            );

            setSelectedProduct(updatedProduct);
            onProductUpdated(updatedProduct);
            setMetadataMessage("Product metadata saved.");
        } catch (err) {
            setMetadataError(
                err.response?.data?.detail ||
                err.message ||
                "Failed to save product metadata."
            );
        } finally {
            setSavingMetadata(false);
        }
    };


    const handleCreateProduct = async () => {
        if (!selectedPipelineRecord || creatingProduct) {
            return;
        }

        if (!newProductTitle.trim()) {
            setCreateProductError("Enter a product title.");
            return;
        }

        const cad = Number(newProductCadPrice);
        const usd = Number(newProductUsdPrice);

        if (
            newProductCadPrice.trim() === "" ||
            newProductUsdPrice.trim() === "" ||
            !Number.isFinite(cad) ||
            !Number.isFinite(usd) ||
            cad <= 0 ||
            usd <= 0
        ) {
            setCreateProductError("Enter valid CAD and USD prices.");
            return;
        }

        setCreatingProduct(true);
        setCreateProductError("");
        setCreateProductMessage("");

        try {
            const response = await createAdminProduct({
                creator_id: "8f11a695-fb32-4309-b6ab-2cb878762e8e",
                storefront_id: "399ebc1e-982a-4225-9a71-47a375a1e857",
                title: newProductTitle.trim(),
                description: "",
                tags: [],
                category: newProductCategory,
                asset_slug: selectedPipelineRecord.design,
                pricing: {
                    cad,
                    usd,
                },
                production_artwork: {},
                variants: [],
            });

            const createdProduct = response.product;

            if (createdProduct) {
                onProductUpdated(createdProduct);
            }

            setCreateProductMessage("Draft product created and linked to its design.");
            setNewProductTitle("");
            setNewProductCadPrice("");
            setNewProductUsdPrice("");
            setSelectedPipelineRecord(null);
        } catch (err) {
            setCreateProductError(
                err.response?.data?.detail ||
                err.message ||
                "Failed to create product."
            );
        } finally {
            setCreatingProduct(false);
        }
    };


    const filteredProducts =
        statusFilter === "ALL"
            ? products
            : products.filter(
                (product) =>
                    product.status === statusFilter
            );

    return (
        <div>

            <div className="mb-6 flex justify-end">
                <button
                    type="button"
                    onClick={() => setShowCreateProduct((current) => !current)}
                    className="border border-matrix-green/40 px-4 py-2 font-mono text-xs uppercase tracking-wider text-matrix-green hover:bg-matrix-green/10"
                >
                    {showCreateProduct ? "Cancel" : "+ Create Product"}
                </button>
            </div>

            {showCreateProduct && (
                <div className="mb-6 border border-matrix-green/20 bg-matrix-green/[0.03] p-5">
                    <p className="font-mono text-xs uppercase tracking-wider text-matrix-green">
                        Create Product from Verified Assets
                    </p>


                    <div className="mt-4 space-y-3">
                        <input
                            type="text"
                            value={newProductTitle}
                            onChange={(event) =>
                                setNewProductTitle(event.target.value)
                            }
                            placeholder="Product title"
                            className="w-full border border-white/20 bg-black px-3 py-2 font-mono text-xs text-white"
                        />

                        <input
                            type="text"
                            value={newProductCategory}
                            onChange={(event) =>
                                setNewProductCategory(event.target.value)
                            }
                            placeholder="Product category"
                            className="w-full border border-white/20 bg-black px-3 py-2 font-mono text-xs text-white"
                        />

                        <div className="grid grid-cols-2 gap-3">
                            <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={newProductCadPrice}
                                onChange={(event) =>
                                    setNewProductCadPrice(event.target.value)
                                }
                                placeholder="Price (CAD)"
                                className="w-full border border-white/20 bg-black px-3 py-2 font-mono text-xs text-white"
                            />

                            <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={newProductUsdPrice}
                                onChange={(event) =>
                                    setNewProductUsdPrice(event.target.value)
                                }
                                placeholder="Price (USD)"
                                className="w-full border border-white/20 bg-black px-3 py-2 font-mono text-xs text-white"
                            />
                        </div>

                        {selectedPipelineRecord && (
                            <p className="font-mono text-xs text-matrix-green">
                                Selected: {selectedPipelineRecord.design}
                            </p>
                        )}

                        {createProductError && (
                            <p className="font-mono text-xs text-red-400">
                                {createProductError}
                            </p>
                        )}

                        {createProductMessage && (
                            <p className="font-mono text-xs text-matrix-green">
                                {createProductMessage}
                            </p>
                        )}

                        <button
                            type="button"
                            disabled={!selectedPipelineRecord || creatingProduct}
                            onClick={handleCreateProduct}
                            className="border border-matrix-green/40 px-4 py-2 font-mono text-xs uppercase text-matrix-green disabled:opacity-40"
                        >
                            {creatingProduct ? "Creating..." : "Create Draft Product"}
                        </button>
                    </div>


                    {assetPipelineRecords.filter((record) => record.verified).length === 0 ? (
                        <p className="mt-4 font-mono text-xs text-white/50">
                            No verified Publish records available.
                        </p>
                    ) : (
                        <div className="mt-4 space-y-3">
                            {assetPipelineRecords
                                .filter((record) => record.verified)
                                .map((record) => (
                                    <div
                                        key={record.design}
                                        className="flex items-center justify-between gap-4 border border-white/10 p-3"
                                    >
                                        <div>
                                            <p className="font-mono text-xs text-white">
                                                {record.design}
                                                {selectedPipelineRecord?.design === record.design && (
                                                    <span className="ml-2 text-matrix-green">SELECTED</span>
                                                )}
                                            </p>
                                            <p className="mt-1 font-mono text-[10px] text-white/40">
                                                {record.artwork_keys?.length || 0} artwork files ·{" "}
                                                {record.mockup_keys?.length || 0} mockups
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => setSelectedPipelineRecord(record)}
                                            className="border border-white/20 px-3 py-2 font-mono text-[10px] uppercase text-white/70"
                                        >
                                            Select
                                        </button>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            )}

            {selectedProduct && (
                <div
                    className="
                        mb-6
                        border
                        border-matrix-green/20
                        bg-matrix-green/[0.03]
                        px-5
                        py-4
                    "
                >
                    <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-matrix-green/60">
                        // EDIT PRODUCT METADATA
                    </p>

                    <p className="mt-2 font-mono text-[8px] uppercase tracking-wider text-white/30">
                        {selectedProduct.product_id}
                    </p>

                    <div className="mt-5 space-y-4">
                        <div>
                            <label className="mb-2 block font-mono text-[9px] uppercase tracking-wider text-white/60">
                                Product Title
                            </label>
                            <input
                                type="text"
                                value={editTitle}
                                onChange={(event) =>
                                    setEditTitle(event.target.value)
                                }
                                className="w-full border border-white/10 bg-black/40 px-3 py-2 font-mono text-sm text-white outline-none focus:border-matrix-green/50"
                            />
                        </div>

                        <div>
                            <label className="mb-2 block font-mono text-[9px] uppercase tracking-wider text-white/60">
                                Description
                            </label>
                            <textarea
                                value={editDescription}
                                onChange={(event) =>
                                    setEditDescription(event.target.value)
                                }
                                rows={5}
                                className="w-full border border-white/10 bg-black/40 px-3 py-2 font-mono text-sm text-white outline-none focus:border-matrix-green/50"
                            />
                        </div>

                        <div>
                            <label className="mb-2 block font-mono text-[9px] uppercase tracking-wider text-white/60">
                                Tags
                            </label>
                            <input
                                type="text"
                                value={editTags}
                                onChange={(event) =>
                                    setEditTags(event.target.value)
                                }
                                placeholder="comedy, graphic tee, parody"
                                className="w-full border border-white/10 bg-black/40 px-3 py-2 font-mono text-sm text-white outline-none focus:border-matrix-green/50"
                            />
                            <p className="mt-1 font-mono text-[8px] text-white/30">
                                Separate tags with commas.
                            </p>
                        </div>

                        {metadataError && (
                            <p className="font-mono text-xs text-red-400">
                                {String(metadataError)}
                            </p>
                        )}

                        {metadataMessage && (
                            <p className="font-mono text-xs text-matrix-green">
                                {metadataMessage}
                            </p>
                        )}

                        <button
                            type="button"
                            onClick={handleSaveMetadata}
                            disabled={savingMetadata}
                            className="border border-matrix-green/40 bg-matrix-green/10 px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-matrix-green transition-colors hover:bg-matrix-green/20 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {savingMetadata
                                ? "Saving..."
                                : "Save Metadata"}
                        </button>
                    </div>
                </div>
            )}


            <section
                className="
                    mb-10
                "
            >

                <div
                    className="
                        border-l-2
                        border-matrix-green/50
                        pl-5
                    "
                >

                    <p
                        className="
                            font-mono
                            text-[9px]
                            uppercase
                            tracking-[0.3em]
                            text-matrix-green/60
                        "
                    >
                        // PRODUCT CATALOG
                    </p>


                    <h3
                        className="
                            mt-3
                            font-display
                            text-4xl
                            font-black
                            uppercase
                            leading-none
                            tracking-[-0.04em]
                            text-white
                            md:text-6xl
                        "
                    >
                        Products
                    </h3>


                    <p
                        className="
                            mt-4
                            max-w-2xl
                            font-mono
                            text-xs
                            leading-6
                            text-white/40
                            md:text-sm
                        "
                    >
                        Manage the Comik Releaf product catalog,
                        publication state, variants, and provider
                        mappings.
                    </p>

                    <div
                        className="
                            mt-6
                            flex
                            flex-wrap
                            items-center
                            gap-2
                        "
                    >

                        {[
                            {
                                label: "ALL",
                                value: "ALL",
                            },
                            {
                                label: "DRAFT",
                                value: "DRAFT",
                            },
                            {
                                label: "ACTIVE",
                                value: "ACTIVE",
                            },
                            {
                                label: "ARCHIVED",
                                value: "ARCHIVED",
                            },
                        ].map((filter) => (
                            <button
                                key={filter.value}
                                type="button"
                                onClick={() =>
                                    setStatusFilter(
                                        filter.value
                                    )
                                }
                                className={`
                                    border
                                    px-4
                                    py-2
                                    font-mono
                                    text-[8px]
                                    uppercase
                                    tracking-[0.2em]
                                    transition
                                    ${
                                        statusFilter ===
                                        filter.value
                                            ? "border-matrix-green/50 bg-matrix-green/[0.08] text-matrix-green"
                                            : "border-white/10 bg-white/[0.02] text-white/30 hover:border-white/20 hover:text-white/60"
                                    }
                                `}
                            >
                                {filter.label}
                            </button>
                        ))}

                    </div>

                </div>

            </section>


            <section
                className="
                    border
                    border-matrix-green/10
                    bg-black/60
                "
            >

                <div
                    className="
                        flex
                        min-h-[360px]
                        items-center
                        justify-center
                        p-8
                    "
                >

                    {loading && (
                        <div
                            className="
                                text-center
                            "
                        >

                            <p
                                className="
                                    font-mono
                                    text-[9px]
                                    uppercase
                                    tracking-[0.25em]
                                    text-matrix-green/60
                                "
                            >
                                // LOADING PRODUCT CATALOG
                            </p>

                        </div>
                    )}


                    {!loading && error && (
                        <div
                            className="
                                max-w-xl
                                text-center
                            "
                        >

                            <p
                                className="
                                    font-mono
                                    text-[9px]
                                    uppercase
                                    tracking-[0.25em]
                                    text-matrix-red
                                "
                            >
                                // PRODUCT API ERROR
                            </p>


                            <p
                                className="
                                    mt-4
                                    font-mono
                                    text-xs
                                    leading-6
                                    text-white/40
                                "
                            >
                                {error}
                            </p>

                        </div>
                    )}


                    {!loading &&
                        !error &&
                        products.length === 0 && (
                            <div
                                className="
                                    max-w-xl
                                    text-center
                                "
                            >

                                <div
                                    className="
                                        mx-auto
                                        flex
                                        h-14
                                        w-14
                                        items-center
                                        justify-center
                                        border
                                        border-matrix-green/40
                                        bg-matrix-green/[0.05]
                                    "
                                >

                                    <span
                                        className="
                                            font-mono
                                            text-xs
                                            text-matrix-green
                                        "
                                    >
                                        01
                                    </span>

                                </div>


                                <p
                                    className="
                                        mt-5
                                        font-mono
                                        text-[9px]
                                        uppercase
                                        tracking-[0.25em]
                                        text-white/30
                                    "
                                >
                                    Product catalog empty
                                </p>


                                <p
                                    className="
                                        mt-2
                                        font-mono
                                        text-[8px]
                                        uppercase
                                        tracking-wider
                                        text-white/15
                                    "
                                >
                                    No products currently exist
                                </p>

                            </div>
                        )}


{!loading &&
    !error &&
    products.length > 0 &&
    filteredProducts.length === 0 && (
        <div
            className="
                max-w-xl
                text-center
            "
        >

            <div
                className="
                    mx-auto
                    flex
                    h-14
                    w-14
                    items-center
                    justify-center
                    border
                    border-white/10
                    bg-white/[0.02]
                "
            >

                <span
                    className="
                        font-mono
                        text-xs
                        text-white/30
                    "
                >
                    00
                </span>

            </div>


            <p
                className="
                    mt-5
                    font-mono
                    text-[9px]
                    uppercase
                    tracking-[0.25em]
                    text-white/30
                "
            >
                No matching products
            </p>


            <p
                className="
                    mt-2
                    font-mono
                    text-[8px]
                    uppercase
                    tracking-wider
                    text-white/15
                "
            >
                No products currently match the selected
                status filter
            </p>

        </div>
    )}

{!loading &&
    !error &&
    products.length > 0 && (
        <div
            className="
                w-full
                overflow-hidden
                border
                border-white/10
                bg-black/20
            "
        >

            <div
                className="
                    grid
                    grid-cols-[2fr_1fr_1fr_1fr_1fr]
                    border-b
                    border-white/10
                    bg-white/[0.02]
                    px-5
                    py-3
                "
            >

                <span
                    className="
                        font-mono
                        text-[8px]
                        uppercase
                        tracking-[0.2em]
                        text-white/30
                    "
                >
                    Product
                </span>

                <span
                    className="
                        font-mono
                        text-[8px]
                        uppercase
                        tracking-[0.2em]
                        text-white/30
                    "
                >
                    Category
                </span>

                <span
                    className="
                        font-mono
                        text-[8px]
                        uppercase
                        tracking-[0.2em]
                        text-white/30
                    "
                >
                    Status
                </span>

                <span
                    className="
                        font-mono
                        text-[8px]
                        uppercase
                        tracking-[0.2em]
                        text-white/30
                    "
                >
                    Visibility
                </span>

                <span
                    className="
                        text-right
                        font-mono
                        text-[8px]
                        uppercase
                        tracking-[0.2em]
                        text-white/30
                    "
                >
                    Variants
                </span>

            </div>


            {filteredProducts.map((product) => (
                    <div
                        key={product.product_id}
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                            setSelectedProduct(product)
                        }
                        className="
                            grid
                            cursor-pointer
                            grid-cols-[2fr_1fr_1fr_1fr_1fr]
                            items-center
                            border-b
                            border-white/5
                            px-5
                            py-4
                            transition
                            hover:bg-white/[0.02]
                            last:border-b-0
                        "
                    >

                    <div>

                        <p
                            className="
                                font-mono
                                text-[10px]
                                uppercase
                                tracking-[0.12em]
                                text-white/80
                            "
                        >
                            {product.title}
                        </p>

                        <p
                            className="
                                mt-1
                                font-mono
                                text-[7px]
                                tracking-wider
                                text-white/20
                            "
                        >
                            {product.product_id}
                        </p>

                    </div>


                    <span
                        className="
                            font-mono
                            text-[8px]
                            uppercase
                            tracking-wider
                            text-white/50
                        "
                    >
                        {product.category}
                    </span>


                    <span
                        className="
                            font-mono
                            text-[8px]
                            uppercase
                            tracking-wider
                            text-matrix-green/70
                        "
                    >
                        {product.status}
                    </span>


                    <span
                        className="
                            font-mono
                            text-[8px]
                            uppercase
                            tracking-wider
                            text-white/50
                        "
                    >
                        {product.visible
                            ? "VISIBLE"
                            : "HIDDEN"}
                    </span>


                    <span
                        className="
                            text-right
                            font-mono
                            text-[9px]
                            text-white/50
                        "
                    >
                        {product.variants?.length || 0}
                    </span>

                </div>
            ))}

        </div>
    )}

                </div>

            </section>

        </div>
    );
}

export default Dashboard;